/**
 * Route to component mappings
 * Maps routes to their respective components based on user role
 */

import { lazy } from "react";
import lazyWithRetry from "./lazyWithRetry";

// Dashboard Components
const CustomerIndex = lazyWithRetry(() => import("pages/customer/Index"));
const VendorIndex = lazyWithRetry(() => import("pages/vendor/Index"));
const RiderIndex = lazyWithRetry(() => import("pages/riders/Index"));
// const CompanyRidersIndex = lazyWithRetry(() =>
//   import("pages/riders/companyRider/Index")
// );
const CompanyIndex = lazyWithRetry(() => import("pages/company/Index"));
const StarrikIndex = lazyWithRetry(() => import("pages/starrik/Index"));

// Orders Components
const OrdersList = lazyWithRetry(() => import("pages/shared/OrdersList"));
const VendorOrders = lazyWithRetry(() => import("pages/vendor/Orders"));
const RiderOrders = lazyWithRetry(() => import("pages/riders/Orders"));
const StarrikTotalOrders = lazyWithRetry(() => import("pages/starrik/Orders"));

// Running Orders (Riders)
const RiderRunningOrder = lazyWithRetry(() =>
  import("pages/riders/RunningOrder")
);

// Other Components
const CompanyRiders = lazy(() => import("pages/company/Riders"));
const CompanyBikesIndex = lazy(() => import("pages/company/CompanyBikesIndex"));
const StarrikUsers = lazy(() => import("pages/starrik/Users"));
const StarrikUserDetails = lazy(() => import("pages/starrik/UserDetails"));
const StarrikEmployees = lazy(() => import("pages/starrik/Employees"));
const StarrikTransactions = lazy(() => import("pages/starrik/Transactions"));

/**
 * Dashboard component mapping by role
 */
export const DASHBOARD_COMPONENTS = {
  customer: CustomerIndex,
  vendor: VendorIndex,
  rider: RiderIndex,
  independentRider: RiderIndex,
  companyRider: RiderIndex,
  company: CompanyIndex,
  admin: StarrikIndex,
};

/**
 * Orders component mapping by role
 */
export const ORDERS_COMPONENTS = {
  customer: OrdersList,
  vendor: VendorOrders,
  rider: RiderOrders,
  independentRider: RiderOrders,
  companyRider: RiderOrders,
  company: OrdersList, // Company might use same as customer
  admin: StarrikTotalOrders,
};

/**
 * Route to component mapping
 * Structure: { route: { role: Component } }
 */
export const ROUTE_COMPONENT_MAP = {
  "/dashboard": DASHBOARD_COMPONENTS,
  "/orders": ORDERS_COMPONENTS,
  "/active-orders": {
    rider: RiderOrders,
    independentRider: RiderOrders,
    companyRider: RiderOrders,
  },
  "/my-running-order": {
    rider: RiderRunningOrder,
    independentRider: RiderRunningOrder,
    companyRider: RiderRunningOrder,
  },
  "/riders": {
    company: CompanyRiders,
  },
  "/bikes": {
    company: CompanyBikesIndex,
  },
  "/users": {
    admin: StarrikUsers,
  },
  "/users/:id": {
    admin: StarrikUserDetails,
  },
  "/employees": {
    admin: StarrikEmployees,
  },
  "/transactions": {
    admin: StarrikTransactions,
  },
};

/**
 * Get component for a route and role
 * @param {string} route - The route path
 * @param {string} role - The user role
 * @returns {React.Component|null} The component or null if not found
 */
export const getComponentForRoute = (route, role) => {
  // Normalize route (handle dynamic segments)
  const normalizedRoute = route.split("?")[0].replace(/\/$/, "") || "/";

  // Check exact match first
  if (ROUTE_COMPONENT_MAP[normalizedRoute]) {
    const component = ROUTE_COMPONENT_MAP[normalizedRoute][role];
    if (component) return component;
  }

  // Check pattern matches (for dynamic routes like /orders/:id)
  for (const [routePattern, roleComponents] of Object.entries(
    ROUTE_COMPONENT_MAP
  )) {
    const patternRegex = new RegExp(
      "^" + routePattern.replace(/:[^/]+/g, "[^/]+") + "$"
    );
    if (patternRegex.test(normalizedRoute)) {
      const component = roleComponents[role];
      if (component) return component;
    }
  }

  return null;
};

/**
 * Check if a route has role-specific components
 * @param {string} route - The route path
 * @returns {boolean}
 */
export const hasRoleSpecificComponents = (route) => {
  const normalizedRoute = route.split("?")[0].replace(/\/$/, "") || "/";
  return !!ROUTE_COMPONENT_MAP[normalizedRoute];
};

